/**
 * Budget / open-commitment tracking.
 *
 * "Open commitment" is the total out-the-door spend if every lot the user is
 * actively bidding on wins (bid amount + fees, per-lot fee resolution).
 * Compared against the optional budget cap from settings so a reseller can
 * see at a glance how much of their buying budget is already spoken for.
 *
 * @module portal/modules/budget
 */

import type { Lot, PortalSettings } from '../types';
import { computeSpend } from '@lotlingo/shared';
import { formatCurrencyDisplay } from './formatting';
import { resolveLotFees, isLotClosed } from './lotOperations';

export interface BudgetSummary {
  /** Total spend if all active bids win. */
  openCommitment: number;
  /** Number of lots with active bids contributing to the commitment. */
  biddingCount: number;
  /** Budget cap from settings; null when unset. */
  budgetCap: number | null;
  /** openCommitment / budgetCap in percent (0–∞); null without a cap. */
  usagePct: number | null;
}

export function computeBudgetSummary(
  lots: Lot[],
  settings: PortalSettings | null | undefined
): BudgetSummary {
  let openCommitment = 0;
  let biddingCount = 0;

  for (const lot of lots) {
    if (!lot || lot.deleted) continue;
    if (!lot.bidding) continue;
    if (typeof lot.bidAmount !== 'number' || !Number.isFinite(lot.bidAmount)) continue;
    // Closed lots are no longer "open" commitments
    if (isLotClosed(lot)) continue;
    openCommitment += computeSpend(lot.bidAmount, resolveLotFees(lot, settings));
    biddingCount += 1;
  }
  openCommitment = Math.round(openCommitment * 100) / 100;

  const rawCap = settings?.budgetCap;
  const budgetCap =
    typeof rawCap === 'number' && Number.isFinite(rawCap) && rawCap > 0 ? rawCap : null;

  return {
    openCommitment,
    biddingCount,
    budgetCap,
    usagePct: budgetCap ? Math.round((openCommitment / budgetCap) * 1000) / 10 : null,
  };
}

/**
 * Render the budget widget into its container.
 * Hidden when the user has no active bids and no cap configured.
 */
export function updateBudgetWidget(
  el: HTMLElement | null,
  lots: Lot[],
  settings: PortalSettings | null | undefined
): void {
  if (!el) return;
  const summary = computeBudgetSummary(lots, settings);

  if (summary.biddingCount === 0 && summary.budgetCap == null) {
    el.hidden = true;
    return;
  }

  const commitment = formatCurrencyDisplay(summary.openCommitment) ?? '$0.00';
  let text = `Open bids: ${commitment}`;
  if (summary.biddingCount > 0) {
    text += ` (${summary.biddingCount})`;
  }
  if (summary.budgetCap != null) {
    const cap = formatCurrencyDisplay(summary.budgetCap) ?? String(summary.budgetCap);
    text += ` of ${cap}`;
  }

  el.textContent = text;
  el.title =
    'Out-the-door spend (incl. fees) if every lot you are bidding on wins' +
    (summary.usagePct != null ? ` — ${summary.usagePct}% of your budget cap` : '');

  el.classList.toggle(
    'budget-widget--over',
    summary.budgetCap != null && summary.openCommitment > summary.budgetCap
  );
  el.classList.toggle(
    'budget-widget--warn',
    summary.budgetCap != null &&
      summary.openCommitment > summary.budgetCap * 0.8 &&
      summary.openCommitment <= summary.budgetCap
  );
  el.hidden = false;
}
